import { IAppConfig } from "../lib/config/app-config";
import { APP_CONFIG } from "../lib/angular/injection-tokens";
import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { Userprofile } from "../_models/Userprofile";
import { UserLoginDetails } from "../_models/UserLoginDetails";
import { AppConstants } from "../constants/constants";
import { Subject } from "rxjs";
import { AddLearnerDto } from "../_models/AddLearnerDto";
import { UpdateLearnerTableDto } from "../_models/UpdateLearnerTableDto";
import { LearnerList } from "../_models/LearnerList";
import { LearnerEditDto } from "../_models/LearnerEditDto";
import { CentreTransferRequestDTO, OrganisationTransferRequestDTO, SubmitTransferResponse } from "../_models/TransferLearner";

@Injectable({
  providedIn: "root",
})
export class UserProfileService {
  private baseUrl: string = this.config.protectedResourceEndpoint;
  onUserDetailsUpdate$: Subject<boolean> = new Subject<boolean>();
  onUserTablesUpdate$: Subject<number[]> = new Subject<number[]>();
  private readonly CREATE_LEARNER: string = "createLearner";
  private readonly EDIT_LEARNER: string = "updatelearner/";
  private static readonly DISPLAY_LEARNER_LIST = "displaylearnerlist";
  private static readonly LEARNER_LIST_LENGTH = "learnerlistlength";
  private static readonly UPDATE_LEARNER_LIST = "updatelearnerlist";
  private static readonly RESET_LEARNER_LIST = "resetlearnerlist";
  private static readonly UPDATE_ACCOUNT_STATUS = "updateaccountstatus";
  private static readonly GET_PAGE_NUMBER = "getcurrentpagenumber";
  private static readonly GROUP_LEARNER_COUNT = "getgrouplearnercount";
  private static readonly DELETE_LEARNER_LIST = "deletelearners";
  private static readonly IS_USER_HAS_ACCESS_ON_LEARNER = "isuserhasaccessonlearner";
  private static readonly FIND_LEARNER_DETAILS = "learner/transfer/findlearnerdetails/";
  private static readonly SUBMIT_TRANSFER_REQUEST = "learner/transfer/submit/";
  private static readonly SUBMIT_TRANSFER_CENTRE_REQUEST = "learner/transfer/centre/submit/";
  private static readonly START_TRANSFER_REQUEST = "learner/transfer/start";
  private static readonly UNDO_TRANSFER_REQUEST = "learner/transfer/undo/";
  private static readonly RESET_MFA = "resetmfa/";
  private static readonly HAS_MFA = "hasmfa/";

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {}

  public getAll$(): Observable<Userprofile[]> {
    return this.http.get<Userprofile[]>(
      this.baseUrl + AppConstants.USER_PROFILE
    );
  }

  public addUserProfile$(userprofile: Userprofile) {
    return this.http.post(
      this.baseUrl + AppConstants.USER_PROFILE,
      userprofile
    );
  }

  public addLearnerProfile$(learnerDto: AddLearnerDto): Observable<Userprofile> {
    return this.http.post<Userprofile>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${this.CREATE_LEARNER}`,
      learnerDto
    );
  }

  public updateLearnerProfile$(learnerEditDto: LearnerEditDto): Observable<LearnerList> {
    return this.http.put<LearnerList>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${this.EDIT_LEARNER}${learnerEditDto.id}`,
      learnerEditDto
    );
  }

  public getLearnerTableRecords$(
    pageIndex: number,
    isInitialLoad: boolean,
    isFromTutorPage: boolean
  ): Observable<LearnerList[]> {
    let params = new HttpParams().set(AppConstants.PAGE_NUMBER, String(+pageIndex + 1));
    if (isInitialLoad)
      params = params.set(AppConstants.IS_INITIAL_LOAD, String(isInitialLoad));

    if(isFromTutorPage)  
      params = params.set(AppConstants.IS_FROM_TUTOR_PAGE, String(isFromTutorPage));

    return this.http.get<LearnerList[]>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.DISPLAY_LEARNER_LIST}`,
      { params }
    );
  }

  public getCurrentPageNumber$(pageIndex: number): Observable<number> {
    let params = new HttpParams().set(AppConstants.PAGE_NUMBER, String(+pageIndex + 1));
    return this.http.get<number>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.GET_PAGE_NUMBER}`,
      { params }
    );
  }

  public getLearnerListLength$(): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.LEARNER_LIST_LENGTH}`
    );
  }

  public updateLearnerTableRecords$(
    tableParameter: UpdateLearnerTableDto,
    isFromTutorPage: boolean
  ): Observable<boolean> {
    let params = new HttpParams();

    if(isFromTutorPage)  
      params = params.set("isFromTutorPage", String(isFromTutorPage));

    return this.http.put<boolean>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.UPDATE_LEARNER_LIST}`,
      tableParameter,
      { params }
    );
  }

  public resetLearnerTable$(): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.RESET_LEARNER_LIST}`
    );
  }

  public getGroupLearnerCount$(): Observable<string> {
    return this.http.get<string>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.GROUP_LEARNER_COUNT}`
    );
  }

  public getUserProfile$(id: number): Observable<Userprofile> {
    return this.http.get<Userprofile>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${id}`
    );
  }

  public getOwnUserProfile$(): Observable<Userprofile> {
    return this.http.get<Userprofile>(
      this.baseUrl + AppConstants.USER_PROFILE + "/me"
    );
  }

  public updateAccountStatus$(userProfileIds: number[], isDisable: boolean): Observable<Object> {
    let params = new HttpParams().set("isDisable", String(isDisable));

    return this.http.put(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.UPDATE_ACCOUNT_STATUS}`, userProfileIds, { params }
    );
  }

  public editUserProfile$(userProfile: Userprofile) {
    return this.http.put(
      this.baseUrl + AppConstants.USER_PROFILE + "/" + userProfile.id,
      userProfile
    );
  }

  public editLoginDetails$(userLoginDetails: UserLoginDetails) {
    return this.http.put(
      this.baseUrl + AppConstants.LOGIN_DETAILS,
      userLoginDetails
    );
  }

  removeUserFromList(
    userprofiles: Userprofile[],
    idsToDelete: number[]
  ): Userprofile[] {
    if (!userprofiles || !idsToDelete) {
      return;
    }

    idsToDelete.forEach((id) => {
      let index = userprofiles.findIndex((data) => data.id === id);
      if (index > -1) {
        userprofiles.splice(index, 1);
      }
    });

    return userprofiles;
  }

  getUpdatedUserKV(
    data: Userprofile[],
    currentLengthPage: number
  ): { [key: number]: Userprofile[] } {
    if (!data || !currentLengthPage) {
      return;
    }

    let result: { [key: number]: Userprofile[] } = {};
    let numberKey = Math.floor(data.length / currentLengthPage);
    numberKey =
      data.length % currentLengthPage !== 0 ? numberKey + 1 : numberKey;

    for (let i: number = 0; i < numberKey; i++) {
      result[i] = [];
      let userprofiles: Userprofile[] = [];
      for (let j: number = 0; j < currentLengthPage; j++) {
        let currentData: Userprofile = data[i * currentLengthPage + j];
        if (!currentData) {
          break;
        }

        userprofiles.push(currentData);
      }

      result[i] = userprofiles;
    }

    return result;
  }

  deleteLearners$(learnersId: number[]): Observable<Object> {
    return this.http.request(
      AppConstants.DELETE,
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.DELETE_LEARNER_LIST}`,
      { body: learnersId }
    );
  }

  isUserHasAccessToLearner$(learnerId: number): Observable<boolean> {
    return this.http.get<boolean>(
      this.baseUrl + AppConstants.USER_PROFILE + AppConstants.SLASH +
      UserProfileService.IS_USER_HAS_ACCESS_ON_LEARNER + 
      AppConstants.SLASH + learnerId
    );
  }

  findLearnerDetails$(usernameOrEmail: string) : Observable<Userprofile> {
    return this.http.get<Userprofile>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.FIND_LEARNER_DETAILS}${usernameOrEmail}`
    );
  }

  submitTransferRequest$(transferRequest: OrganisationTransferRequestDTO) : Observable<SubmitTransferResponse> {
    return this.http.post<SubmitTransferResponse>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.SUBMIT_TRANSFER_REQUEST}`,
      transferRequest
    );
  }

  submitTransferCentreRequest$(transferRequest: CentreTransferRequestDTO) : Observable<SubmitTransferResponse> {
    return this.http.post<SubmitTransferResponse>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.SUBMIT_TRANSFER_CENTRE_REQUEST}`,
      transferRequest
    );
  }

  startTransfer$() : Observable<boolean> {
    return this.http.put<boolean>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.START_TRANSFER_REQUEST}`, null
    );
  }

  undoTransfer$(transferId: number) : Observable<boolean> {
    return this.http.put<boolean>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.UNDO_TRANSFER_REQUEST}${transferId}`, null
    );
  }

  resetMfa$(userId: number) : Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.RESET_MFA}${userId}`
    );
  }

  hasMfa$(userId: number) : Observable<boolean> {
    return this.http.get<boolean>(
      `${this.baseUrl}${AppConstants.USER_PROFILE}${AppConstants.SLASH}${UserProfileService.HAS_MFA}${userId}`
    );
  }

}
